import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useQuery } from '@tanstack/react-query';
import Loader from 'components/Kizen/Loader';
import { PageSizing } from 'components/Layout/PageContentWidth';
import { getBusinessLicenseQuery, getSummaryUsageQuery } from 'query/billing';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { StatisticRow } from './components/StatisticRow';
import { Button } from '@kizen/kds/Button';
import { ContentCard, PageWrapper } from '../styles';
import { ObjectCard } from './components/ObjectCard';
import { UsageSection } from './components/UsageSection';
import { AmountRow } from './components/AmountRow';
import useModal from '__components/Modals/useModal';
import Modal from 'components/Modals/presets/BasicModal';
import { Link } from 'react-router-dom';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { BillingSummary } from '@kizen/api/billing';
import { AiUsageModal } from '../AiUsageModal';
import { formatBillingCurrency } from '../util';
import { getBudgetStats, getCountStats } from './stats';

type Money = BillingSummary['accrual_limit'];

const formatUsageDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  return new Date(value).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

type UsageContentProps = {
  showOverageSummary?: boolean;
  showManageUsersLink?: boolean;
};

export const UsageContent = ({
  showOverageSummary = true,
  showManageUsersLink = true,
}: UsageContentProps = {}) => {
  const { t } = useTranslation();

  const { data: summaryData, isLoading: summaryUsageLoading } = useQuery(
    getSummaryUsageQuery()
  );

  const { data: businessLicenses, isLoading: businessLicensesLoading } =
    useQuery(getBusinessLicenseQuery());

  const {
    canManagePlans,
    canEditPaymentMethods,
    canViewUsage,
    canSelfServeBilling,
  } = useBillingPermissions();

  const generalPlan = useMemo(() => {
    return businessLicenses?.find(
      (l) => l.license_plan.plan_category === 'general'
    );
  }, [businessLicenses]);

  const isAnnualBilling = generalPlan?.billing_cycle === 'year';
  const monthlyResetDate = formatUsageDate(summaryData?.next_reset_date);
  const nextInvoiceDate = formatUsageDate(summaryData?.next_invoice_date);
  // Annual plans need the real invoice date; without it, omit the line rather
  // than imply the invoice comes on the monthly reset date.
  const showInvoiceLine =
    canSelfServeBilling && (!isAnnualBilling || Boolean(nextInvoiceDate));

  const aiCreditsMonthlyStats = useMemo(
    () =>
      getCountStats(
        summaryData?.core_platform?.ai_credits?.monthly_usage,
        summaryData?.core_platform?.ai_credits?.monthly_limit
      ),
    [summaryData]
  );

  const automationMonthlyStats = useMemo(
    () =>
      getCountStats(
        summaryData?.automations?.monthly_usage,
        summaryData?.automations?.monthly_limit
      ),
    [summaryData]
  );

  const automationDailyStats = useMemo(
    () =>
      getCountStats(
        summaryData?.automations?.daily_usage,
        summaryData?.automations?.daily_limit
      ),
    [summaryData]
  );

  const emailMonthlyStats = useMemo(
    () =>
      getCountStats(
        summaryData?.emails?.monthly_usage,
        summaryData?.emails?.monthly_limit
      ),
    [summaryData]
  );

  const platformUserMonthlyStats = useMemo(
    () =>
      getCountStats(
        summaryData?.core_platform?.users_usage,
        summaryData?.core_platform?.max_users
      ),
    [summaryData]
  );

  const overallOverageStats = useMemo(
    () => getBudgetStats(summaryData?.core_platform),
    [summaryData]
  );

  const aiCreditsOverageStats = useMemo(
    () => getBudgetStats(summaryData?.core_platform?.ai_credits),
    [summaryData]
  );

  const automationOverageStats = useMemo(
    () => getBudgetStats(summaryData?.automations),
    [summaryData]
  );

  const marketingOverageStats = useMemo(
    () => getBudgetStats(summaryData?.emails),
    [summaryData]
  );

  const passthroughOverageStats = useMemo(
    () => getBudgetStats(summaryData?.core_platform?.passthrough),
    [summaryData]
  );

  const accruedCharges = useMemo(() => {
    const amountOf = (section?: {
      currently_accrued_non_invoiced_overage_amount?: Money;
    }) => section?.currently_accrued_non_invoiced_overage_amount?.amount ?? 0;

    return {
      aiCredits: amountOf(summaryData?.core_platform?.ai_credits),
      automations: amountOf(summaryData?.automations),
      emails: amountOf(summaryData?.emails),
      passthrough: amountOf(summaryData?.core_platform?.passthrough),
    };
  }, [summaryData]);

  const nextInvoiceStats = useMemo(() => {
    const accrualLimit = summaryData?.accrual_limit?.amount ?? 0;
    const symbol = summaryData?.accrual_limit?.symbol ?? '$';
    // Round to the cent: float addition can drift
    const accrued =
      Math.round(
        (accruedCharges.aiCredits +
          accruedCharges.automations +
          accruedCharges.emails +
          accruedCharges.passthrough) *
          100
      ) / 100;

    if (!accrualLimit) {
      return {
        percentage: 0,
        used: accrued,
        limit: Infinity,
        currencySymbol: symbol,
      };
    }

    const percentage = (accrued / accrualLimit) * 100;

    return {
      percentage: Math.min(percentage, 100),
      used: accrued,
      limit: accrualLimit,
      currencySymbol: symbol,
    };
  }, [summaryData, accruedCharges]);

  const accrualLimitText = formatBillingCurrency(
    summaryData?.accrual_limit?.amount ?? 0,
    true,
    summaryData?.accrual_limit?.symbol ?? '$'
  );

  const customObjectStats = useMemo(() => {
    const objectKeys = Object.keys(summaryData?.custom_objects ?? {});
    const objects = objectKeys.map((key) => {
      const matchingObject = (summaryData?.custom_objects as any)[key];

      const { stats, object } = matchingObject;

      const fieldPercentage = stats.fields_limit
        ? (stats.fields_usage / stats.fields_limit) * 100
        : 0;

      const fieldsStats = {
        percentage: fieldPercentage,
        used: stats.fields_usage ?? 0,
        limit: stats.fields_limit ?? Infinity,
        isOver: fieldPercentage >= 100,
      };

      const recordPercentage = stats.records_limit
        ? (stats.records_usage / stats.records_limit) * 100
        : 0;

      const recordStats = {
        percentage: recordPercentage,
        used: stats.records_usage ?? 0,
        limit: stats.records_limit ?? Infinity,
        isOver: recordPercentage >= 100,
      };

      return {
        key,
        object,
        stats,
        fieldsStats,
        recordStats,
      };
    });

    return objects.sort((a, b) => {
      const aMaxPercentage = Math.max(
        a.fieldsStats.percentage,
        a.recordStats.percentage
      );
      const bMaxPercentage = Math.max(
        b.fieldsStats.percentage,
        b.recordStats.percentage
      );

      return bMaxPercentage - aMaxPercentage;
    });
  }, [summaryData]);

  const customObjectSummaryStats = useMemo(() => {
    const numObjects = Object.keys(summaryData?.custom_objects || {}).length;

    const numObjectsAtLimit = customObjectStats.filter(
      (s) => s.fieldsStats.isOver || s.recordStats.isOver
    ).length;

    const numObjectsNearLimit = customObjectStats.filter((s) => {
      const isAtRisk =
        s.fieldsStats.percentage >= 80 || s.recordStats.percentage >= 80;
      const isOver =
        s.fieldsStats.percentage >= 100 || s.recordStats.percentage >= 100;

      return isAtRisk && !isOver;
    }).length;

    const numObjectsOk = customObjectStats.filter(
      (s) => s.fieldsStats.percentage < 80 && s.recordStats.percentage < 80
    ).length;

    const percentObjectsAtLimit = (numObjectsAtLimit / numObjects) * 100;

    const percentObjectsNearLimit = (numObjectsNearLimit / numObjects) * 100;

    const configuredObjectLimit = (generalPlan?.config?.limits as any)
      ?.custom_object_limit;

    const objectLimit = configuredObjectLimit ?? Infinity;

    const usedPercent = configuredObjectLimit
      ? (numObjects / configuredObjectLimit) * 100
      : 100;

    return {
      limit: objectLimit,
      used: numObjects,
      percentage: usedPercent,
      isOver: false,
      divisions: {
        risk: percentObjectsNearLimit,
        over: percentObjectsAtLimit,
      },
      numObjectsAtLimit,
      numObjectsNearLimit,
      numObjectsOk,
    };
  }, [summaryData, customObjectStats, generalPlan]);

  const objectsAtLimits = useMemo(() => {
    return customObjectStats.filter(
      (s) => s.fieldsStats.isOver || s.recordStats.isOver
    );
  }, [customObjectStats]);

  const [allObjectsModalProps, , { show: showAllObjectsModal }] = useModal();
  const [aiUsageModalProps, , { show: showAiUsageModal }] = useModal();

  if (summaryUsageLoading || businessLicensesLoading) {
    return <Loader loading />;
  }

  const objectCountLabel = (count: number) =>
    `${count} ${count === 1 ? t('Object') : t('Objects')}`;

  return (
    <PageSizing>
      <div className="pb-20">
        <PageWrapper>
          {/* The right column keeps a minimum width so its rows don't wrap on smaller screens. */}
          <div className="grid grid-cols-[minmax(0,2.5fr)_minmax(360px,1fr)] gap-20">
            <div className="flex flex-col gap-20">
              <ContentCard data-qa-id="billing-usage-plan-usage">
                <Typography variant="header" size="base" weight="semibold">
                  {t('Plan Usage')}
                </Typography>
                {monthlyResetDate ? (
                  <>
                    <Spacer size={15} />
                    {/* One block so both lines share Typography's line-height. */}
                    <Typography>
                      {showInvoiceLine
                        ? t('Monthly metrics next reset on {{date}}.', {
                            date: monthlyResetDate,
                          })
                        : t('Monthly metrics next reset on {{date}}', {
                            date: monthlyResetDate,
                          })}
                      {showInvoiceLine ? (
                        <>
                          <br />
                          {isAnnualBilling
                            ? t(
                                'Next annual invoice will be issued on {{date}}.',
                                { date: nextInvoiceDate }
                              )
                            : t(
                                'Next monthly invoice will be issued on the same day.'
                              )}
                        </>
                      ) : null}
                    </Typography>
                  </>
                ) : null}
              </ContentCard>

              <UsageSection
                icon="heading-ai-credits"
                title={t('Kizen Native AI — Credits Used')}
                qaId="billing-usage-ai-credits"
                action={
                  canViewUsage ? (
                    <Button
                      variant="text"
                      color="secondary"
                      size="small"
                      onClick={showAiUsageModal}
                      qa={{ id: 'view-ai-usage-button' }}
                    >
                      {t('View AI Usage')}
                    </Button>
                  ) : null
                }
              >
                <StatisticRow
                  {...aiCreditsMonthlyStats}
                  label={t('Monthly')}
                  emphasize
                  qaId="billing-usage-ai-credits-monthly"
                />
              </UsageSection>

              <UsageSection
                icon="heading-agentic-workflows"
                title={t('Agentic Workflows — Steps Executed')}
                qaId="billing-usage-agentic-workflows"
              >
                <StatisticRow
                  {...automationMonthlyStats}
                  label={t('Monthly')}
                  emphasize
                  qaId="billing-usage-agentic-workflows-monthly"
                />
                <Spacer size={15} />
                <StatisticRow
                  {...automationDailyStats}
                  label={t('Daily')}
                  info={t('Usage is tracked in the UTC timezone')}
                  qaId="billing-usage-agentic-workflows-daily"
                />
              </UsageSection>

              <UsageSection
                icon="heading-marketing-platform"
                title={t('Marketing Platform — Emails Sent')}
                qaId="billing-usage-marketing-platform"
              >
                <StatisticRow
                  {...emailMonthlyStats}
                  label={t('Monthly')}
                  emphasize
                  qaId="billing-usage-marketing-platform-monthly"
                />
              </UsageSection>

              <UsageSection
                icon="heading-custom-objects"
                title={t('Custom Objects')}
                qaId="billing-usage-custom-objects"
                action={
                  <Button
                    variant="text"
                    color="secondary"
                    size="small"
                    onClick={showAllObjectsModal}
                    qa={{ id: 'view-all-object-usage-link' }}
                  >
                    {t('View All Object Usage')}
                  </Button>
                }
              >
                <StatisticRow
                  {...customObjectSummaryStats}
                  label={t('Total Custom Objects')}
                  emphasize
                  qaId="billing-usage-custom-objects-total"
                />
                <Spacer size={15} />
                <div className="grid grid-cols-[auto_1fr] gap-x-10 gap-y-10">
                  <Typography>{t('In good standing')}:</Typography>
                  <Typography color="button-primary-hover" weight="bold">
                    {objectCountLabel(customObjectSummaryStats.numObjectsOk)}
                  </Typography>
                  <Typography>{t('At risk (80% capacity)')}:</Typography>
                  <Typography color="feedback-warning" weight="bold">
                    {objectCountLabel(
                      customObjectSummaryStats.numObjectsNearLimit
                    )}
                  </Typography>
                  <Typography>{t('At limit(s)')}:</Typography>
                  <Typography color="button-warning-default" weight="bold">
                    {objectCountLabel(
                      customObjectSummaryStats.numObjectsAtLimit
                    )}
                  </Typography>
                </div>
                {objectsAtLimits.length > 0 ? (
                  <>
                    <Spacer size={30} />
                    <Typography weight="semibold">
                      {t('Objects at Limit(s)')}
                    </Typography>
                    <Spacer size={20} />
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-20">
                      {objectsAtLimits.map((entry) => {
                        return (
                          <ObjectCard
                            key={entry.key}
                            object={entry.object}
                            fieldStats={entry.fieldsStats}
                            recordStats={entry.recordStats}
                          />
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </UsageSection>
            </div>
            <div className="flex flex-col gap-20">
              <div>
                <ContentCard data-qa-id="billing-usage-user-summary">
                  <Typography variant="header" size="base" weight="semibold">
                    {t('User Summary')}
                  </Typography>
                  <Spacer size={20} />
                  <StatisticRow
                    {...platformUserMonthlyStats}
                    label={t('Platform Users')}
                    qaId="billing-usage-platform-users"
                  />
                  {canManagePlans && showManageUsersLink ? (
                    <>
                      <Spacer size={30} />
                      <div className="flex justify-center">
                        <Link to="/settings/billing/plan">
                          <Button color="secondary">{t('Manage Users')}</Button>
                        </Link>
                      </div>
                    </>
                  ) : null}
                </ContentCard>
              </div>
              {showOverageSummary ? (
                <div>
                  <ContentCard data-qa-id="billing-usage-overage-summary">
                    <Typography variant="header" size="base" weight="semibold">
                      {t('Accrued Overage Charges')}
                    </Typography>
                    <Spacer size={20} />
                    <Typography size="md" weight="semibold">
                      {t('Charges Across Monthly Budgets')}
                    </Typography>
                    <Spacer size={20} />
                    <StatisticRow
                      {...overallOverageStats}
                      label={t('Overall')}
                      qaId="billing-usage-overage-overall"
                    />
                    <Spacer size={20} />
                    <StatisticRow
                      {...aiCreditsOverageStats}
                      label={t('Kizen AI Credits Overage')}
                      qaId="billing-usage-overage-ai-credits"
                    />
                    <Spacer size={20} />
                    <StatisticRow
                      {...automationOverageStats}
                      label={t('Agentic Workflows Overage')}
                      qaId="billing-usage-overage-agentic-workflows"
                    />
                    <Spacer size={20} />
                    <StatisticRow
                      {...marketingOverageStats}
                      label={t('Marketing Platform Overage')}
                      qaId="billing-usage-overage-marketing-platform"
                    />
                    <Spacer size={20} />
                    <StatisticRow
                      {...passthroughOverageStats}
                      label={t('Use of External LLMs (via Kizen)')}
                      qaId="billing-usage-overage-passthrough"
                    />
                    {canEditPaymentMethods ? (
                      <>
                        <Spacer size={30} />
                        <div className="flex justify-center">
                          <Link to="/settings/billing/details">
                            <Button
                              color="secondary"
                              qa={{ id: 'manage-budgets-button' }}
                            >
                              {t('Manage Budgets')}
                            </Button>
                          </Link>
                        </div>
                      </>
                    ) : null}
                    <Spacer size={30} />
                    <Typography size="md" weight="semibold">
                      {t('Charges Not Yet Invoiced')}
                    </Typography>
                    <Spacer size={20} />
                    <StatisticRow
                      {...nextInvoiceStats}
                      label={t('Next Additional Usage Invoice')}
                      info={t(
                        'Accrued charges will be billed at end of day when they exceed the invoice minimum amount of {{amount}}',
                        { amount: accrualLimitText }
                      )}
                      qaId="billing-usage-next-invoice"
                    />
                    <Spacer size={20} />
                    <div className="flex flex-col gap-y-10">
                      <AmountRow
                        label={t('Kizen AI Credits Overage')}
                        amount={accruedCharges.aiCredits}
                        currencySymbol={nextInvoiceStats.currencySymbol}
                        qaId="billing-usage-accrued-ai-credits"
                      />
                      <AmountRow
                        label={t('Agentic Workflows Overage')}
                        amount={accruedCharges.automations}
                        currencySymbol={nextInvoiceStats.currencySymbol}
                        qaId="billing-usage-accrued-agentic-workflows"
                      />
                      <AmountRow
                        label={t('Marketing Platform Overage')}
                        amount={accruedCharges.emails}
                        currencySymbol={nextInvoiceStats.currencySymbol}
                        qaId="billing-usage-accrued-marketing-platform"
                      />
                      <AmountRow
                        label={t('Use of External LLMs (via Kizen)')}
                        amount={accruedCharges.passthrough}
                        currencySymbol={nextInvoiceStats.currencySymbol}
                        qaId="billing-usage-accrued-passthrough"
                      />
                    </div>
                  </ContentCard>
                </div>
              ) : null}
            </div>
          </div>
        </PageWrapper>
      </div>
      <Modal
        {...allObjectsModalProps}
        heading={t('All Object Usage')}
        size="large"
        buttonText={t('Close')}
        actionBtnColor="blue"
        leftBtn={null}
      >
        <div className="grid grid-cols-3 gap-20">
          {customObjectStats.map((entry) => {
            return (
              <ObjectCard
                key={entry.key}
                object={entry.object}
                fieldStats={entry.fieldsStats}
                recordStats={entry.recordStats}
              />
            );
          })}
        </div>
      </Modal>
      <AiUsageModal {...aiUsageModalProps} />
    </PageSizing>
  );
};
